(() => {
  const rows = [...document.querySelectorAll('[data-fp-lazy-row]')];
  if (!rows.length) return;

  const rootPath = window.Shopify?.routes?.root || '/';
  const currency = window.Shopify?.currency?.active || 'USD';
  const moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency
  });

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const productUrl = (handle) => `${rootPath}products/${encodeURIComponent(handle)}`;

  const sizedImageUrl = (source, width) => {
    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  };

  const getImage = (product) => {
    const image = product.images?.[0] || product.image;
    if (!image) return null;
    return typeof image === 'string'
      ? { src: image, width: 900, height: 900 }
      : { src: image.src, width: image.width || 900, height: image.height || 900 };
  };

  const getPrices = (product) => {
    const variants = product.variants || [];
    const variant = variants.find((item) => item.available) || variants[0];
    return {
      price: Number.parseFloat(variant?.price) || 0,
      compareAt: Number.parseFloat(variant?.compare_at_price) || 0
    };
  };

  const renderCard = (product) => {
    const title = escapeHtml(product.title);
    const href = productUrl(product.handle);
    const image = getImage(product);
    const { price, compareAt } = getPrices(product);
    const onSale = compareAt > price;
    const srcset = image?.src
      ? [300, 450, 600, 750, 900].map((width) => `${escapeHtml(sizedImageUrl(image.src, width))} ${width}w`).join(', ')
      : '';
    const imageMarkup = image?.src
      ? `<img
          src="${escapeHtml(sizedImageUrl(image.src, 900))}"
          srcset="${srcset}"
          sizes="(min-width: 990px) 25vw, 50vw"
          loading="lazy"
          decoding="async"
          fetchpriority="low"
          width="${Number(image.width) || 900}"
          height="${Number(image.height) || 900}"
          alt="${title}"
          class="fp-entity-sections__image"
        >`
      : '<span class="fp-entity-sections__image"></span>';

    return `<article class="fp-entity-sections__card" data-carousel-card>
      <a class="fp-entity-sections__media" href="${href}" aria-label="${title}">
        ${imageMarkup}
        ${onSale ? '<span class="fp-entity-sections__badge">Sale</span>' : ''}
      </a>
      <div class="fp-entity-sections__details">
        <h3><a href="${href}">${title}</a></h3>
        <div class="fp-entity-sections__price">
          ${onSale ? `<s>${moneyFormatter.format(compareAt)}</s>` : ''}
          <span>${moneyFormatter.format(price)}</span>
        </div>
      </div>
    </article>`;
  };

  const renderCarousel = (row, products) => {
    const viewportId = escapeHtml(row.dataset.viewportId);
    return `<fp-product-carousel class="fp-entity-sections__carousel">
      <button class="fp-entity-sections__nav-button fp-entity-sections__nav-button--previous" type="button" aria-label="Previous products" aria-controls="${viewportId}" data-carousel-previous disabled><span aria-hidden="true">&lt;</span></button>
      <div class="fp-entity-sections__viewport" id="${viewportId}" data-carousel-viewport tabindex="0">
        <div class="fp-entity-sections__grid" role="list" data-carousel-track>${products.map(renderCard).join('')}</div>
      </div>
      <button class="fp-entity-sections__nav-button fp-entity-sections__nav-button--next" type="button" aria-label="Next products" aria-controls="${viewportId}" data-carousel-next><span aria-hidden="true">&gt;</span></button>
    </fp-product-carousel>`;
  };

  const showError = (row) => {
    const placeholder = row.querySelector('[data-fp-lazy-placeholder]');
    if (!placeholder) return;
    placeholder.classList.add('fp-entity-sections__lazy-placeholder--error');
    placeholder.innerHTML = '<button class="fp-entity-sections__lazy-retry" type="button">Load products</button>';
    placeholder.querySelector('button')?.addEventListener('click', () => loadRow(row), { once: true });
  };

  const loadRow = async (row) => {
    if (row.dataset.lazyState === 'loading' || row.dataset.lazyState === 'loaded') return;
    row.dataset.lazyState = 'loading';
    try {
      const handle = encodeURIComponent(row.dataset.collectionHandle);
      const response = await fetch(`${rootPath}collections/${handle}/products.json?limit=12`, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Products request failed: ${response.status}`);
      const payload = await response.json();
      const products = Array.isArray(payload.products) ? payload.products.slice(0, 12) : [];
      const placeholder = row.querySelector('[data-fp-lazy-placeholder]');
      if (!placeholder || !products.length) throw new Error('No products returned');
      placeholder.outerHTML = renderCarousel(row, products);
      row.dataset.lazyState = 'loaded';
    } catch (error) {
      row.dataset.lazyState = 'error';
      showError(row);
    }
  };

  if (!('IntersectionObserver' in window) || window.Shopify?.designMode) {
    rows.forEach(loadRow);
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      loadRow(entry.target);
    });
  }, { rootMargin: '300px 0px' });
  rows.forEach((row) => observer.observe(row));
})();
